import { hashValue } from '@orbomb/content';
import type { SimConfig } from './config';
import type {
  ExplosionState,
  GoldenBallState,
  MatchResult,
  MatchState,
  PlayerBallState,
  TotemState,
} from './types';

/** JSON-compatible snapshot. The state contains only plain data by construction. */
export type SerializedMatchState = MatchState;

/**
 * Structural copy of match state. JSON.parse(JSON.stringify) allocates huge
 * strings on every client snapshot/prediction clone and is a GC stall source.
 */
export function cloneState(state: MatchState): MatchState {
  return {
    simulationVersion: state.simulationVersion,
    contentHash: state.contentHash,
    balanceHash: state.balanceHash,
    mapId: state.mapId,
    seed: state.seed,
    tick: state.tick,
    rng: state.rng,
    players: [clonePlayer(state.players[0]!), clonePlayer(state.players[1]!)],
    goldens: state.goldens.map(cloneGolden),
    totems: [cloneTotem(state.totems[0]!), cloneTotem(state.totems[1]!)],
    explosions: state.explosions.map(cloneExplosion),
    nextExplosionId: state.nextExplosionId,
    bonuses: state.bonuses.map((bonus) => ({ ...bonus })),
    nextBonusId: state.nextBonusId,
    clock: { ...state.clock },
    spawnRegion: state.spawnRegion,
    stats: { ...state.stats },
    result: state.result ? cloneResult(state.result) : null,
  };
}

function clonePlayer(player: PlayerBallState): PlayerBallState {
  return { ...player };
}

function cloneGolden(golden: GoldenBallState): GoldenBallState {
  return { ...golden };
}

function cloneTotem(totem: TotemState): TotemState {
  return { ...totem };
}

function cloneExplosion(explosion: ExplosionState): ExplosionState {
  return {
    ...explosion,
    cross: {
      ...explosion.cross,
      reach: { ...explosion.cross.reach },
      horizontal: { ...explosion.cross.horizontal },
      vertical: { ...explosion.cross.vertical },
      basesHit: explosion.cross.basesHit.slice(),
    },
    hitTargets: explosion.hitTargets.slice(),
  };
}

function cloneResult(result: MatchResult): MatchResult {
  return {
    ...result,
    score: { ...result.score },
    totemHp: { ...result.totemHp },
  };
}

export function serializeState(state: MatchState): string {
  return JSON.stringify(state);
}

export class IncompatibleStateError extends Error {}

export function restoreState(
  snapshot: string | SerializedMatchState,
  config: SimConfig,
): MatchState {
  const state = (
    typeof snapshot === 'string' ? JSON.parse(snapshot) : cloneState(snapshot)
  ) as MatchState;
  if (state.simulationVersion !== config.simulationVersion) {
    throw new IncompatibleStateError(
      `snapshot simulation ${state.simulationVersion} != ${config.simulationVersion}`,
    );
  }
  if (state.contentHash !== config.contentHash) {
    throw new IncompatibleStateError(
      `snapshot content ${state.contentHash} != ${config.contentHash}`,
    );
  }
  return state;
}

/** Canonical hash of every rule-relevant field (positions, velocities, timers, RNG...). */
export function hashState(state: MatchState): string {
  return hashValue(state);
}

export interface FieldDifference {
  path: string;
  expected: unknown;
  actual: unknown;
}

/** Lists differing leaf fields between two states (used for first-divergence reports). */
export function diffStates(
  expected: unknown,
  actual: unknown,
  path = '',
  limit = 20,
): FieldDifference[] {
  const out: FieldDifference[] = [];
  const walk = (a: unknown, b: unknown, p: string) => {
    if (out.length >= limit) return;
    if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
      const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
      for (const key of [...keys].sort()) {
        walk(
          (a as Record<string, unknown>)[key],
          (b as Record<string, unknown>)[key],
          p ? `${p}.${key}` : key,
        );
      }
      return;
    }
    const same =
      typeof a === 'number' && typeof b === 'number' ? a === b || (a === 0 && b === 0) : a === b;
    if (!same) out.push({ path: p, expected: a, actual: b });
  };
  walk(expected, actual, path);
  return out;
}
